/**
 * Visual design templates for the printed question paper.
 *
 * Each template supplies Tailwind class tokens + a font family that the editor
 * threads into the paper header, section headings, end-of-paper footer and page
 * container. Changing the template restyles the preview AND the exported PDF
 * (same DOM is captured), without touching the questions themselves.
 *
 * The three designs deliberately echo the three looks common on Bangladeshi
 * exam papers:
 *   • Classic  — the everyday school paper: a ruled masthead box, underlined
 *                section headings, a centred "সমাপ্ত" at the end.
 *   • Formal   — a board/government style: a full framed masthead box, serif
 *                small-caps, solid shaded section bands.
 *   • Minimal  — a modern coaching-centre worksheet: one hairline rule,
 *                left-aligned meta, quiet accent-bar headings.
 */

export const DEFAULT_TEMPLATE_ID = 'classic';

export const PAPER_TEMPLATES = [
    {
        id: 'classic',
        label: 'Classic',
        labelBn: 'ক্লাসিক',
        fontFamily: "'Noto Sans Bengali', sans-serif",
        header: {
            // Centered masthead (no outer frame) — clean traditional school look.
            wrap: 'mb-4 px-4 py-2.5 text-center',
            title: 'text-center text-xl font-bold uppercase tracking-wide text-zinc-900',
            titlePx: 20,
            metaStacked: true, // subject / class each on their own centered line
            subject: 'mt-1 text-center text-[13.5px] font-semibold text-zinc-800',
            klass: 'mt-0.5 text-center text-[13px] font-semibold text-zinc-800',
            row: 'mt-1.5 flex items-center justify-between gap-3 border-t border-zinc-400 pt-1.5 text-[12.5px] font-medium text-zinc-800',
            // Optional free-text line (instructions) under the whole masthead.
            note: 'mt-2 text-center text-[12px] italic leading-snug text-zinc-700',
        },
        // Bold heading underlined across the column.
        section: 'mb-2 mt-5 border-b border-zinc-300 pb-1 text-[13px] font-bold text-zinc-800 first:mt-0',
        // Centred end-mark with a rule above it.
        footer: 'mt-6 border-t border-zinc-400 pt-2 text-center text-[12.5px] font-bold tracking-wide text-zinc-700',
    },
    {
        id: 'formal',
        label: 'Formal',
        labelBn: 'ফর্মাল',
        fontFamily: "'Noto Serif Bengali', 'Noto Sans Bengali', serif",
        header: {
            // Board-exam masthead — a full outer frame (box all around) with a
            // faint tint, serif small-caps title. Clearly boxed vs. Classic's
            // open centred masthead.
            wrap: 'mb-4 rounded-sm border-2 border-zinc-900 bg-zinc-50 px-4 py-3 text-center',
            title: 'text-center text-2xl font-extrabold uppercase tracking-[0.18em] text-zinc-900',
            titlePx: 24,
            metaStacked: true,
            subject: 'mt-1.5 text-center text-sm font-bold text-zinc-900',
            klass: 'mt-0.5 text-center text-[13px] font-semibold text-zinc-800',
            // Meta row split off by a rule inside the frame.
            row: 'mt-2 flex items-center justify-between gap-3 border-t border-zinc-400 pt-2 text-[12.5px] font-semibold text-zinc-800',
            // Centred instruction line, ruled off to echo a board rubric.
            note: 'mt-2 border-t border-zinc-300 pt-1.5 text-center text-[12px] font-medium leading-snug text-zinc-800',
        },
        // Solid shaded band heading, full-width — the signature board-paper look,
        // unmistakably different from Classic's thin underline.
        section: 'mb-2.5 mt-6 rounded-sm bg-zinc-800 px-3 py-1.5 text-center text-[13px] font-bold uppercase tracking-[0.12em] text-white first:mt-0',
        footer: 'mt-7 border-y-2 border-double border-zinc-800 py-2 text-center text-[13px] font-extrabold uppercase tracking-[0.2em] text-zinc-800',
    },
    {
        id: 'minimal',
        label: 'Minimal',
        labelBn: 'মিনিমাল',
        fontFamily: "'Noto Sans Bengali', sans-serif",
        header: {
            // Modern, left-aligned, a single hairline rule; meta on one inline row.
            wrap: 'mb-4 border-b border-zinc-300 pb-2',
            title: 'text-left text-lg font-bold text-zinc-900',
            titlePx: 18,
            metaStacked: false, // class · subject · time · marks all on one inline row
            row: 'mt-1 flex flex-wrap items-center gap-x-4 gap-y-0.5 text-[12px] text-zinc-600',
            // Quiet left-aligned instruction line to match the minimal look.
            note: 'mt-1.5 text-left text-[11.5px] leading-snug text-zinc-500',
        },
        // Quiet heading with a short accent bar on the left.
        section: 'mb-1.5 mt-4 border-l-2 border-zinc-800 pl-2 text-[12.5px] font-semibold uppercase tracking-wide text-zinc-700 first:mt-0',
        footer: 'mt-5 text-center text-[11px] font-medium uppercase tracking-[0.25em] text-zinc-400',
    },
];

export const getTemplate = (id) =>
    PAPER_TEMPLATES.find((t) => t.id === id) || PAPER_TEMPLATES[0];
